import type { AcademicTerm, ClassRole, Classroom } from "@classroom/domain";
import { i18n } from "../i18n";
import { buildScopedRecordReport, type ExportRange, type RecordExportSource } from "./localRecordExport";
import type { RecordExportTab, RecordReport } from "./recordExports";

const sections = {
  roster: { tab: "roster" },
  answers: { tab: "records", sheet: 1 },
  materials: { tab: "records", sheet: 0 },
  coursework: { tab: "homework" },
  attendance: { tab: "attendance" },
  missingHomework: { tab: "homeroomHomework" },
  routine: { tab: "studentRecords", sheet: 2 },
  affairs: { tab: "homeroom" },
  duties: { tab: "homeroomDuties" },
  seating: { tab: "seating" },
  calendar: { tab: "calendar" },
} satisfies Record<string, { tab: RecordExportTab; sheet?: number }>;

export type AdminRecordKind = keyof typeof sections;
export const RECORD_EXPORT_KINDS = Object.keys(sections) as AdminRecordKind[];

export const ROLE_RECORD_KINDS = {
  teaching: ["answers", "materials", "coursework"],
  homeroom: ["attendance", "missingHomework", "routine", "affairs", "duties"],
} satisfies Record<ClassRole, AdminRecordKind[]>;
export const SHARED_RECORD_KINDS: AdminRecordKind[] = ["roster"];

export function recordKindsForRole(role: ClassRole): AdminRecordKind[] {
  return [...ROLE_RECORD_KINDS[role], ...SHARED_RECORD_KINDS];
}

export function buildAdminRecordReport(
  classes: Classroom[],
  source: RecordExportSource,
  kinds: AdminRecordKind[],
  range: ExportRange,
  term?: AcademicTerm,
): RecordReport {
  const sheets: RecordReport["sheets"] = [];
  const usedTitles = new Set<string>();
  for (const classroom of classes) {
    for (const kind of new Set(kinds)) {
      if (kind !== "calendar" && !recordKindsForRole(classroom.classRole).includes(kind)) continue;
      const section: { tab: RecordExportTab; sheet?: number } = sections[kind];
      const report = buildScopedRecordReport(section.tab, classroom, source, range, undefined, term);
      const selected =
        section.sheet === undefined ? report.sheets : report.sheets.slice(section.sheet, section.sheet + 1);
      for (const item of selected) {
        const base = `${classroom.name} · ${item.title}`;
        let title = base;
        let suffix = 2;
        while (usedTitles.has(title)) title = `${base} (${suffix++})`;
        usedTitles.add(title);
        sheets.push({ ...item, title });
      }
    }
  }
  return {
    title: `${i18n.t("adminRecords:exportTitle")}${term ? `｜${term.schoolYear} ${term.name}` : ""}${range.from || range.to ? `｜${range.from || "…"}–${range.to || "…"}` : ""}`,
    className: classes.map((classroom) => classroom.name).join("、"),
    sheets,
  };
}
